import React, { useEffect, useState } from 'react';
import ReportNote from '../components/ReportNote';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

import { localDateStr, monthRange, fmtDate } from './utils';

function AgeBadge({ days }) {
  const color = days >= 30 ? 'var(--critical)' : days >= 7 ? 'var(--warn)' : 'var(--ok)';
  return (
    <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 12, fontWeight: 700, color }}>
      {days}d
    </span>
  );
}

export default function IncidentReport() {
  const [offset, setOffset] = useState(0);
  const [range, setRange] = useState(monthRange(0));
  const [custom, setCustom] = useState(false);
  const [fromDate, setFromDate] = useState(monthRange(0).from);
  const [toDate, setToDate] = useState(monthRange(0).to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('open');

  const load = (f, t) => {
    setLoading(true);
    fetch(`${API}/api/reports/incidents?from_date=${f}&to_date=${t}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (!custom) {
      const r = monthRange(offset);
      setRange(r); setFromDate(r.from); setToDate(r.to);
      load(r.from, r.to);
    }
  }, [offset, custom]);

  const exportCSV = () => {
    window.location.href = `${API}/api/reports/export/csv?type=incidents&from_date=${fromDate}&to_date=${toDate}`;
  };

  const tabStyle = (t) => ({
    padding: '7px 18px', border: 'none', cursor: 'pointer', fontWeight: 600,
    fontSize: 13, borderRadius: '7px 7px 0 0',
    background: tab === t ? 'var(--surface)' : 'transparent',
    color: tab === t ? 'var(--accent)' : 'var(--text-2)',
    borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
  });

  return (
    <div>
      <ReportNote what="Incidents created and resolved, broken down by priority." from={fromDate} to={toDate} />
      {/* Controls */}
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {!custom ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setOffset(o => o - 1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>‹</button>
            <span style={{ fontWeight: 700, fontSize: 15, minWidth: 180, textAlign: 'center' }}>{range.label}</span>
            <button onClick={() => setOffset(o => o + 1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>›</button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="input" style={{ width: 140 }} />
            <span style={{ color: 'var(--text-2)' }}>→</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="input" style={{ width: 140 }} />
            <button onClick={() => load(fromDate, toDate)} className="btn btn-primary" style={{ padding: '6px 14px' }}>Apply</button>
          </div>
        )}
        <button onClick={() => setCustom(c => !c)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>
          {custom ? 'Monthly view' : 'Custom range'}
        </button>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button onClick={exportCSV} className="btn btn-secondary">↓ CSV</button>
          <button onClick={() => window.print()} className="btn btn-secondary">🖨 Print / PDF</button>
        </div>
      </div>

      {loading && <div className="loading">Loading incident data…</div>}

      {!loading && data && (
        <>
          {/* Summary */}
          <div className="grid-4" style={{ marginBottom: 24 }}>
            <div className="card">
              <div className="card-title">Open Incidents</div>
              <div className="card-value" style={{ color: data.open_count > 5 ? 'var(--critical)' : 'var(--warn)' }}>
                {data.open_count}
              </div>
              <div className="card-sub">current state</div>
            </div>
            <div className="card">
              <div className="card-title">Resolved This Period</div>
              <div className="card-value" style={{ color: 'var(--ok)' }}>{data.closed_count}</div>
              <div className="card-sub">{fromDate} → {toDate}</div>
            </div>
            <div className="card">
              <div className="card-title">Avg Resolution Time</div>
              <div className="card-value" style={{ fontSize: 22 }}>
                {data.avg_resol_days != null ? `${data.avg_resol_days}d` : '—'}
              </div>
            </div>
            <div className="card">
              <div className="card-title">Aging (open)</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--ok)', fontFamily: 'Syne, sans-serif' }}>{data.aging_buckets.lt7}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-3)' }}>&lt;7d</div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--warn)', fontFamily: 'Syne, sans-serif' }}>{data.aging_buckets.lt30}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-3)' }}>7–30d</div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--critical)', fontFamily: 'Syne, sans-serif' }}>{data.aging_buckets.gt30}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-3)' }}>&gt;30d</div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="card" style={{ padding: 0 }}>
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 4px' }}>
              <button style={tabStyle('open')} onClick={() => setTab('open')}>
                Open ({data.open_count})
              </button>
              <button style={tabStyle('closed')} onClick={() => setTab('closed')}>
                Resolved ({data.closed_count})
              </button>
            </div>

            <div className="table-wrap">
              {tab === 'open' && (
                <table>
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Summary</th>
                      <th>Assignee</th>
                      <th>Priority</th>
                      <th style={{ textAlign: 'right' }}>Age</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.open.map(r => (
                      <tr key={r.key} className={r.age_days >= 30 ? 'tr-critical' : ''}>
                        <td>
                          {r.url
                            ? <a href={r.url} target="_blank" rel="noopener noreferrer" className="issue-key-link">{r.key}</a>
                            : <span className="issue-key-link">{r.key}</span>}
                        </td>
                        <td style={{ fontSize: 13, maxWidth: 400 }}>{r.summary}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.assignee}</td>
                        <td>
                          <span className={`badge badge-${(r.priority || '').toLowerCase()}`}>{r.priority || 'Unset'}</span>
                        </td>
                        <td style={{ textAlign: 'right' }}><AgeBadge days={r.age_days} /></td>
                      </tr>
                    ))}
                    {data.open.length === 0 && (
                      <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32, color: 'var(--text-2)' }}>No open incidents</td></tr>
                    )}
                  </tbody>
                </table>
              )}

              {tab === 'closed' && (
                <table>
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Summary</th>
                      <th>Assignee</th>
                      <th>Priority</th>
                      <th>Resolved</th>
                      <th style={{ textAlign: 'right' }}>Days to resolve</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.closed.map(r => (
                      <tr key={r.key}>
                        <td>
                          {r.url
                            ? <a href={r.url} target="_blank" rel="noopener noreferrer" className="issue-key-link">{r.key}</a>
                            : <span className="issue-key-link">{r.key}</span>}
                        </td>
                        <td style={{ fontSize: 13, maxWidth: 400 }}>{r.summary}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.assignee}</td>
                        <td>
                          <span className={`badge badge-${(r.priority || '').toLowerCase()}`}>{r.priority || 'Unset'}</span>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{fmtDate(r.resolved_at)}</td>
                        <td style={{ textAlign: 'right' }}><AgeBadge days={r.resol_days} /></td>
                      </tr>
                    ))}
                    {data.closed.length === 0 && (
                      <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--text-2)' }}>No resolved incidents in this period</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .sidebar, .topbar { display: none !important; }
          .main { margin: 0 !important; padding: 20px !important; }
        }
      `}</style>
    </div>
  );
}
